import React from 'react';
import * as Yup from 'yup';
import { Button, Dialog, DialogProps } from '@economic/taco';

import { Trans } from '@lingui/macro';
import { Column, Row } from '../../shared/components/Grid/Grid';
import { Form, Field } from '../../shared/components/Form';
import { FormProps } from '../types';
import internalApiClient from '../../shared/util/api/internalApiClient';
import { t, useLocalization } from '../../shared/components/l10n/l10n';
import Loader from '../../shared/components/Loader';
import { ProductCategory } from '../../../types/internal-api';

type ProductCategoryFormProps = FormProps<ProductCategory>;
type ProductCategoryDialogProps = Omit<DialogProps, 'children'> & ProductCategoryFormProps;

const initialValues: Partial<ProductCategory> = {
    name: undefined,
    variant1: undefined,
    variant2: undefined,
};

const validationSchema = () =>
    Yup.object({
        name: Yup.string().required(),
        variant1: Yup.string().nullable(),
        variant2: Yup.string().nullable(),
    });

export default function CreateProductCategoryForm({
    initialData = initialValues,
    notifications,
    onSubmitted,
    onSubmitError,
    onCancel,
}: ProductCategoryFormProps) {
    const loading = useLocalization(textIds);

    // ProductCategory has id/number, variant1, and variant2, which is required in the type but
    // they aren't required while submitting form
    const handleSubmit = async (values: Partial<ProductCategory>) => {
        try {
            const response = await internalApiClient.post(`/products/categories`, values);
            return Promise.resolve(response);
        } catch (error) {
            error.validation = error.response.data;
            return Promise.reject(error);
        }
    };

    return loading ? (
        <div className="h-40">
            <Loader />
        </div>
    ) : (
        <Form
            initialData={initialData}
            messages={notifications}
            onSubmit={handleSubmit}
            onSubmitted={onSubmitted}
            onSubmitError={onSubmitError}
            validationSchema={validationSchema}>
            <h1>{t`FORM_CATEGORY_HEADLINE`}</h1>

            <Row>
                <Column>
                    <Field.Text label={t`FORM_CATEGORY_EDITFIELD_CATEGORY`} name="name" required />
                    <Field.Text label={t`FORM_CATEGORY_EDITFIELD_VARIANT1`} name="variant1" defaultValue="size" />
                    <Field.Text label={t`FORM_CATEGORY_EDITFIELD_VARIANT2`} name="variant2" defaultValue="colour" />
                </Column>
            </Row>

            <div className="form__buttons">
                <Button type="submit" appearance="primary">
                    <Trans>Save</Trans>
                </Button>
                {onCancel && (
                    <Button onClick={onCancel} appearance="default">
                        <Trans>Cancel</Trans>
                    </Button>
                )}
            </div>
        </Form>
    );
}

export const CreateProductCategoryDialog = ({ onSubmitted: handleSubmitted, ...props }: ProductCategoryDialogProps) => (
    <Dialog {...props} size="xs">
        <Dialog.Content aria-label={t`FORM_CATEGORY_CREATETEXT`}>
            {({ close }) => (
                <CreateProductCategoryForm
                    onCancel={close}
                    onSubmitted={productCategory => {
                        if (handleSubmitted) {
                            handleSubmitted(productCategory);
                        }

                        close();
                    }}
                />
            )}
        </Dialog.Content>
    </Dialog>
);

const textIds = ['FORM_CATEGORY_EDITFIELD_CATEGORY', 'FORM_CATEGORY_EDITFIELD_VARIANT1', 'FORM_CATEGORY_EDITFIELD_VARIANT2'];
